import { create } from 'zustand'

// frontend-restructuring §1a — the global screen recorder, lifted wholesale out of
// `Home.jsx` (a ~110-line `handleRecordToggle` + a `trigger-screen-record` /
// `screen-record-finished` window-CustomEvent bus).
//
// One recorder for the whole app. Any surface can start/stop it (`toggle()`); when an upload
// finishes, `finishedTick` bumps and `lastUrl` holds the new link. Each caller keeps its own
// local "I started this" flag and, on a tick bump, decides whether to consume `lastUrl` —
// exactly the guard the old `screen-record-finished` broadcast needed (`Home.jsx` comment:
// "cada instancia solo actúa si fue ELLA quien puso isRecording en true").
//
// `MediaRecorder` / `getDisplayMedia` can't be exercised by the smoke or unit suites — the
// record→upload→embed flow needs a manual pass.

let mediaRecorder = null
let mediaChunks = []
let micTrack = null

function playAudioTone(type) {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    if (type === 'start') {
      osc.frequency.setValueAtTime(600, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.2)
    } else {
      osc.frequency.setValueAtTime(800, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.2)
    }
    gain.gain.setValueAtTime(0.1, ctx.currentTime)
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2)
    osc.start()
    osc.stop(ctx.currentTime + 0.2)
  } catch {
    /* audio tone is best-effort */
  }
}

export const useScreenRecordStore = create((set, get) => ({
  recording: false,
  micEnabled: true,
  lastUrl: null,
  finishedTick: 0,

  setMicEnabled: (value) => {
    const on = !!value
    if (micTrack) micTrack.enabled = on
    set({ micEnabled: on })
  },

  toggle: async () => {
    if (get().recording) {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop()
      set({ recording: false, micEnabled: false })
      return
    }

    let stream
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: 'always' }, audio: true })
    } catch (err) {
      console.error('Screen record permission denied or error:', err)
      return
    }

    let micStream = null
    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      console.warn('User denied or no mic available for recording', e)
    }

    // Delay logic: wait 2s before starting.
    setTimeout(() => {
      if (!stream.active) return

      const tracks = [...stream.getVideoTracks()]
      let actx = null
      let dest = null

      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext
        actx = new AudioContext()
        dest = actx.createMediaStreamDestination()

        if (stream.getAudioTracks().length > 0) {
          actx.createMediaStreamSource(new MediaStream([stream.getAudioTracks()[0]])).connect(dest)
        }
        if (micStream && micStream.getAudioTracks().length > 0) {
          micTrack = micStream.getAudioTracks()[0]
          micTrack.enabled = true
          actx.createMediaStreamSource(new MediaStream([micTrack])).connect(dest)
        }
        if (dest.stream.getAudioTracks().length > 0) tracks.push(dest.stream.getAudioTracks()[0])
      } catch (e) {
        console.error('Audio mix fallback', e)
        if (stream.getAudioTracks()[0]) tracks.push(stream.getAudioTracks()[0])
        if (micStream && micStream.getAudioTracks()[0]) {
          micTrack = micStream.getAudioTracks()[0]
          micTrack.enabled = true
          tracks.push(micTrack)
        }
      }

      const recorder = new MediaRecorder(new MediaStream(tracks), { mimeType: 'video/webm' })
      mediaRecorder = recorder
      mediaChunks = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) mediaChunks.push(e.data) }

      recorder.onstop = () => {
        playAudioTone('stop')
        stream.getTracks().forEach((t) => t.stop())
        if (micStream) micStream.getTracks().forEach((t) => t.stop())
        if (actx) actx.close().catch((e) => console.warn(e))
        mediaRecorder = null
        micTrack = null
        set({ recording: false, micEnabled: false })

        const blob = new Blob(mediaChunks, { type: 'video/webm' })
        // El dueño lo pone el backend con `req.user.id` (POST /api/recordings/upload) --
        // antes el cliente mandaba `userId` con el nick, que `StorageFile.created_by` (INTEGER)
        // rechazaba (bug preexistente, encontrado al automatizar pending-manual-checks #1).
        const formData = new FormData()
        formData.append('file', blob, 'screen_recording.webm')

        console.log('Subiendo grabacion...')
        fetch('/api/recordings/upload', { method: 'POST', body: formData })
          .then((res) => res.json())
          .then((data) => {
            if (data.success) {
              set((s) => ({ lastUrl: data.data.link, finishedTick: s.finishedTick + 1 }))
              console.log('Grabacion subida con exito.')
            } else {
              console.error('Error backend al subir grabacion:', data.error)
            }
          })
          .catch((err) => console.error('Error de render/upload:', err))
      }

      recorder.start()
      playAudioTone('start')
      set({ recording: true, micEnabled: true, lastUrl: null })

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state !== 'inactive') recorder.stop()
        set({ recording: false, micEnabled: false })
      }
    }, 2000)
  },
}))
